import React, { useMemo, useState } from "react";
import Offcanvas from "react-bootstrap/Offcanvas";
import Container from "react-bootstrap/Container";
import { buildMallaGridModel } from "../../utils/buildMallaRenderMap";
import {
  adaptPosgradoMateriasForGrid,
  adaptPosgradoMetaForGrid,
  isPosgradoMateria,
} from "../../utils/adaptPosgradoMalla";
import { computeMallaTotals } from "../../utils/curriculumMalla";
import { formatMallaCourseStats } from "../../utils/mallaCourseDisplay";
import MallaSummary from "./MallaSummary";
import PosgradoMallaSummary from "./PosgradoMallaSummary";
import MallaLegend from "./MallaLegend";
import MallaGrid from "./MallaGrid";
import MallaMobileList from "./MallaMobileList";

function MallaCurricular({
  malla,
  materias,
  meta,
  variant = "licenciatura",
}) {
  const [selected, setSelected] = useState(null);
  const isPosgrado = variant === "posgrado";

  const gridMaterias = useMemo(
    () =>
      isPosgrado ? adaptPosgradoMateriasForGrid(materias) : materias,
    [materias, isPosgrado]
  );

  const gridMeta = useMemo(
    () =>
      isPosgrado ? adaptPosgradoMetaForGrid(meta, malla) : meta,
    [meta, malla, isPosgrado]
  );

  const totals = useMemo(
    () => computeMallaTotals(gridMaterias),
    [gridMaterias]
  );

  const gridModel = useMemo(
    () =>
      gridMaterias?.length ? buildMallaGridModel(gridMaterias, gridMeta) : null,
    [gridMaterias, gridMeta]
  );

  const handleSelectMateria = (materia, trayecto) => {
    setSelected({ materia, trayecto });
  };

  const handleCloseDrawer = () => setSelected(null);

  if (!materias?.length) {
    return (
      <section className="course-detail__malla-empty">
        <Container fluid>
          <p>Sin malla curricular disponible por el momento.</p>
        </Container>
      </section>
    );
  }

  const legendTitle = isPosgrado ? "Ejes formativos" : "Trayectos formativos";
  const rowHeaderLabel = isPosgrado ? "Eje" : "Trayecto";

  return (
    <>
      {isPosgrado ? (
        <PosgradoMallaSummary
          malla={malla}
          semestreCount={gridMeta.semestres.length}
        />
      ) : (
        <MallaSummary
          vigencia={malla.Vigencia}
          faseCount={gridMeta.fases?.length ?? 0}
          semestreCount={gridMeta.semestres.length}
          totals={totals}
        />
      )}

      <MallaLegend meta={gridMeta} title={legendTitle} />

      <div className="course-detail__click-hint">
        <Container fluid>
          <p>
            <span className="course-detail__click-hint__icon" aria-hidden="true">
              ↗
            </span>
            Selecciona una materia en la malla para ver el detalle completo.
          </p>
        </Container>
      </div>

      {gridModel && (
        <>
          <MallaGrid
            gridModel={gridModel}
            mallaTitulo={malla.Titulo}
            onSelectMateria={handleSelectMateria}
            rowHeaderLabel={rowHeaderLabel}
            showPhaseRow={!isPosgrado || (gridMeta.fases?.length ?? 0) > 0}
          />
          <MallaMobileList
            materias={gridMaterias}
            meta={gridMeta}
            mallaTitulo={malla.Titulo}
            onSelectMateria={handleSelectMateria}
            axisLabel={rowHeaderLabel}
          />
        </>
      )}

      <Offcanvas show={!!selected} onHide={handleCloseDrawer} placement="end">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Asignatura</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {selected && (
            <div
              className="course-detail__malla-drawer"
              style={{
                borderLeftColor: selected.trayecto?.color,
              }}
            >
              <span className="course-detail__mobile-materias__trayecto">
                {selected.trayecto?.label}
              </span>
              {selected.materia.Clave && (
                <p className="course-detail__malla-drawer__clave">
                  {selected.materia.Clave}
                </p>
              )}
              <h4>{selected.materia.Titulo}</h4>
              <p>{formatMallaCourseStats(selected.materia)}</p>
              <p className="course-detail__malla-drawer__meta">
                Semestre {selected.materia.Semestre}
                {selected.materia.Fase && !isPosgradoMateria(selected.materia)
                  ? ` · ${selected.materia.Fase}`
                  : ""}
              </p>
            </div>
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}

export default MallaCurricular;
